import type { ChainSelectionExtendPayload } from '@/components/app/shapes/shape-text-editor/hooks/use-shape-text-selection'
import { getShapeTextFrame } from '@/components/app/shapes/shape-text-frame-registry'
import {
  frameLocalToGlobalOffset,
  globalToFrameLocalOffset,
} from '@/lib/text-linking/locate-chain-caret-destination'
import { wordBoundaryOffset } from '@/lib/shapes/shape-text-navigation'

/**
 * New chain-global focus offset for a shifted-navigation key (WMPCM-9200).
 *
 * Every unit resolves against the *focus*, never the textarea — once a cross-frame selection exists
 * the textarea sits collapsed at the anchor, so asking it where the focus is returns the wrong end.
 * That is what made Shift+End and Shift+Ctrl+Arrow appear to "reset" the selection.
 *
 * Row-shaped units (`line`, `lineBoundary`) delegate to the frame that owns the focus, because a row
 * here is a laid-out row of that frame's real outline — already narrowed by its wrap bands — and only
 * that frame's `ShapeTextEditor` holds the layout. `word` runs on the chain's concatenated text
 * instead, so a word split across a seam steps once rather than stalling at the boundary.
 */
export function resolveChainFocusOffset(
  frameIds: readonly string[],
  frameLengths: number[],
  total: number,
  currentFocus: number,
  payload: ChainSelectionExtendPayload
): number {
  const { direction, unit } = payload

  if (unit === 'documentBoundary') return direction === 1 ? total : 0
  if (unit === 'character') return currentFocus + direction

  if (unit === 'word') {
    const text = frameIds.map((id) => getShapeTextFrame(id)?.getPlainText() ?? '').join('')

    return wordBoundaryOffset(text, currentFocus, direction)
  }

  const { frameIndex, localOffset } = globalToFrameLocalOffset(frameLengths, currentFocus)
  const handle = getShapeTextFrame(frameIds[frameIndex] ?? '')

  // No live handle (a member scrolled out of the tree, or a spread mid-switch): fall back to a
  // character step rather than parking the focus where it already is, which would read as a dead key.
  if (!handle) return currentFocus + direction

  if (unit === 'lineBoundary') {
    return frameLocalToGlobalOffset(
      frameLengths,
      frameIndex,
      handle.rowBoundaryOffset(localOffset, direction)
    )
  }

  const withinFrame = handle.verticalStepOffset(localOffset, direction)

  if (withinFrame != null) {
    return frameLocalToGlobalOffset(frameLengths, frameIndex, withinFrame)
  }

  // Ran out of rows here, so the step continues into the adjacent member — entering its first row
  // going down, its last going up, at the x the caret left with. This is the case the earlier
  // Left/Right-only implementation punted on by leaving Up/Down to the textarea.
  const neighbourIndex = frameIndex + direction
  const neighbourId = frameIds[neighbourIndex]
  const neighbour = neighbourId != null ? getShapeTextFrame(neighbourId) : undefined

  if (!neighbour) return direction === 1 ? total : 0

  const x = handle.caretXAtOffset(localOffset) ?? 0

  return frameLocalToGlobalOffset(
    frameLengths,
    neighbourIndex,
    neighbour.rowEdgeOffsetNearestX(x, direction === 1 ? 'first' : 'last')
  )
}
